/**
 * marketScaleNarrative — 12/02/2026
 *
 * Événements narratifs "presse spécialisée" déclenchés quand la boîte du
 * joueur pèse suffisamment lourd pour que le marché la remarque. Alignés
 * sur les seuils de `computeMarketScale` (utils/marketScale.js) : chaque
 * seuil traverse pousse UN message dans la boîte de réception.
 *
 * Objectif pédagogique : rendre visible le scaling silencieux appliqué
 * aux concurrents et aux AO. Le joueur voit la conséquence "presse" qui
 * accompagne son ascension, plutôt que de subir un durcissement invisible.
 *
 * Seuils (unique fire par partie) :
 *   • 1.15  → "Étoile montante" (concurrent scale ~+15 %, phase early)
 *   • 1.30  → "Menace crédible" (~+30 %, mid-game)
 *   • 1.45  → "Prédateur alpha" (~+45 %, late-game)
 */
import { marketScaleFromState } from '../../utils/marketScale';

const THRESHOLDS = [
  {
    at: 1.15,
    subject: 'Étoile montante du transport régional',
    body: (companyName) =>
      `Nos analystes ont repéré la percée fulgurante de **${companyName}** sur le paysage logistique. `
      + `Vos concurrents commencent à muscler leur commercial et à réviser leurs grilles tarifaires — c'est le prix à payer pour être remarqué.\n\n`
      + `À surveiller dans les prochains mois : les Appels d'Offres deviennent un peu plus généreux (marché conscient de votre poids), mais les concurrents historiques renforcent leur défense.\n\n`
      + `— La rédaction, Actu Transport`,
  },
  {
    at: 1.30,
    subject: 'Menace crédible pour les mastodontes du fret',
    body: (companyName) =>
      `**${companyName}** ne relève plus de l'anecdote. Selon nos sources, plusieurs directeurs commerciaux concurrents ont convoqué des comités de riposte. `
      + `Attendez-vous à des offres agressives sur vos clients et à un scaling notable de leurs KPIs (flotte, effectif, réputation).\n\n`
      + `Bonne nouvelle : les AO nationaux vous ouvrent leurs portes avec des primes bonifiées. Mauvaise nouvelle : les rachats hostiles coûtent désormais beaucoup plus cher.\n\n`
      + `— La rédaction, Actu Transport`,
  },
  {
    at: 1.45,
    subject: 'Prédateur alpha — le secteur retient son souffle',
    body: (companyName) =>
      `**${companyName}** est officiellement passé du statut de challenger à celui de leader régional incontournable. `
      + `L'ensemble du marché a répliqué : concurrents massivement recapitalisés, primes AO au plafond (jusqu'à +65 %), et vigilance accrue de l'Autorité de la concurrence.\n\n`
      + `À ce niveau, chaque décision stratégique pèse sur l'écosystème entier. Bonne route — nous vous garderons à l'œil.\n\n`
      + `— La rédaction, Actu Transport`,
  },
];

/**
 * Renvoie le payload à pousser via `pushCustomMessage` si un nouveau seuil
 * vient d'être franchi, sinon `null`. Utilise `state.marketScaleFiredThresholds`
 * (array de nombres) comme mémoire persistante.
 *
 * @returns {{ payload, threshold } | null}
 */
export function buildMarketScaleNarrative(state) {
  if (!state) return null;
  const { competitorScale } = marketScaleFromState(state);
  const fired = new Set(state.marketScaleFiredThresholds || []);
  const companyName = state?.company?.name || 'Votre entreprise';
  // On ne déclenche QUE le plus haut palier non encore franchi (évite les
  // triples envois si le joueur saute plusieurs seuils d'un coup).
  const candidate = THRESHOLDS
    .filter(t => competitorScale >= t.at && !fired.has(t.at))
    .sort((a, b) => b.at - a.at)[0];
  if (!candidate) return null;

  return {
    threshold: candidate.at,
    payload: {
      senderType: 'news',
      senderName: 'Actu Transport',
      senderRole: 'Rédaction',
      subject: candidate.subject,
      body: candidate.body(companyName),
      tone: 'neutral',
      kind: 'info',
      category: 'news',
      ttlDays: 14,
      // Trace pour debug / tests.
      __marketScaleThreshold: candidate.at,
    },
  };
}
